<template>
  <div class="pet-desc-page" :style="{ backgroundImage: `url(${designUrl}${isMobile ? 'AddPetPage_phone.png' : 'AddPetPage.jpeg'})` }">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-header" v-if="pet">
      <h1 class="page-title">{{ pet.pet_name }}'s Profile</h1>
    </div>

    <div class="calendar-button-wrapper">
      <button @click="goToCalendar" class="calendar-button">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
      </button>
    </div>

    <div class="page-wrapper" v-if="pet">
      <div class="main-card">

        <div class="profile-row">
          <img :src="backendUrl + '/pet-pics/' + pet.pet_photo" :alt="pet.pet_name" class="pet-img" />
          <div class="pet-info-col">
            <h1 class="pet-name">{{ pet.pet_name }}</h1>
            <p class="pet-breed">{{ pet.breed }}</p>
            <div class="badges-row">
              <span class="badge badge-age">{{ pet.age }}</span>
              <span class="badge" :class="pet.gender === 'Female' ? 'badge-female' : 'badge-male'">{{ pet.gender }}</span>
              <span class="badge badge-vax" title="Vaccinated">
                <img src="/vax.png" class="vax-icon" alt="Vaccinated" />
              </span>
            </div>
            <p v-if="pet.location" class="pet-location">📍 {{ formatLocation (pet.location) }}</p>
            <p v-if="getPetDistance() !== null" class="pet-location">{{ getPetDistance() }} km away</p>
            <p v-if="ownerPhone" class="pet-location">Owner's Phone: {{ ownerPhone }}</p>
          </div>
        </div>

        <div class="divider"></div>

        <div class="inner-section" v-if="pet.description">
          <p class="section-title">About</p>
          <p class="section-text">{{ pet.description }}</p>
        </div>

        <div class="inner-section" v-if="pet.available_days && pet.available_days.length">
          <p class="section-title">Available Days</p>
          <div class="days-row">
            <span
              v-for="day in (Array.isArray(pet.available_days) ? pet.available_days : pet.available_days.split(',').map(d => d.trim()))"
              :key="day"
              class="day-badge"
            >{{ day.slice(0, 3) }}</span>
          </div>
        </div>
        <div class="inner-section" v-if="pet.available_start_time">
          <p class="section-title">Available Time</p>
          <div class="time-slots">
            <div class="time-slot">{{ formatTime(pet.available_start_time) }}</div>
            <div class="time-sep">—</div>
            <div class="time-slot">{{ formatTime(pet.available_end_time) }}</div>
          </div>
        </div>

        <button class="book-btn" @click="bookAppointment">
          Book a Playdate
        </button>

      </div>
    </div>

    <div class="page-wrapper" v-else>
      <p class="loading-text">Loading pet details...</p>
    </div>

  </div>
</template>


<script>
  const backendUrl = process.env.VUE_APP_API_URL
export default {
  props: ['pet', 'user'],
  data() {
    return {
      backendUrl,
      sidebarOpen: false,
      petsOpen: false,
      designUrl: '/background_designs/',
      isMobile: window.innerWidth <= 480,
      ownerPhone: null,
      userCoords: null,
    };
  },
  methods: {
    deg2rad(d) { return d * Math.PI / 180; },
    getPetDistance() {
      if (!this.userCoords || !this.pet?.location_coords) return null;
      try {
        const [lat, lon] = this.pet.location_coords.split(',').map(s => parseFloat(s.trim()));
        if (isNaN(lat) || isNaN(lon)) return null;
        const dLat = this.deg2rad(lat - this.userCoords.lat);
        const dLon = this.deg2rad(lon - this.userCoords.lon);
        const a = Math.sin(dLat/2)**2 +
                  Math.cos(this.deg2rad(this.userCoords.lat)) *
                  Math.cos(this.deg2rad(lat)) *
                  Math.sin(dLon/2)**2;
        return Math.round(6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
      } catch { return null; }
    },
    formatLocation(location) {
      if (!location) return 'Unknown location';
      const parts = location.split(',').map(p => p.trim());
      return parts.slice(0, -3).join(', ');
    },
    goToCalendar() {
      this.$emit('change-page', 'CalendarPage');
    },
    bookAppointment() {
      this.$emit('change-page', 'BookPetAppointmentPage', { pet: this.pet });
    },
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 480;
    },
    formatTime(timeStr) {
      if (!timeStr) return '';
      let [h, m] = timeStr.split(':').map(Number);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
    },
    async fetchOwnerPhone() {
      if (!this.pet?.user_id) return;
      try {
        const res = await fetch(`${backendUrl}/api/auth/users/${this.pet.user_id}`, { credentials: 'include' });
        const data = await res.json();
        this.ownerPhone = data.phone_number || null;
      } catch (e) {
        console.error('Failed to fetch owner phone:', e);
      }
    },
  },
  mounted() {
    console.log('Pet received in description page:', this.pet);
    window.addEventListener('resize', this.handleResize);
    this.fetchOwnerPhone();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => { this.userCoords = { lat: pos.coords.latitude, lon: pos.coords.longitude }; },
        () => {}
      );
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.pet-desc-page {
  min-height: 100vh;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  background-attachment: scroll;
  background-color: #fffbe8;
  padding: 0;
}

.page-wrapper {
  width: 100%;
  max-width: 900px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.main-card {
  width: 100%;
  flex: 1;                /* ← fills all remaining vertical space */
  background: #ffffff;
  border-radius: 28px;
  border: 1px solid #f0e0ff;
  padding: 40px;
  margin: 0;
  box-shadow: 0 4px 24px rgba(186, 113, 255, 0.1);
  display: flex;
  flex-direction: column;
}

/* push book button to bottom */
.book-btn {
  margin-top: auto;
  padding: 17px;
  background: #eac7ff;
  color: #4a1070;
  border: none;
  border-radius: 16px;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  transition: background 0.18s, transform 0.1s;
  letter-spacing: 0.01em;
  font-family: inherit;
}

.book-btn:hover  { background: #ba71ff; color: #fff; }
.book-btn:active { transform: scale(0.98); }

.page-header {
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 20px 0 24px;
}

.badge-vax {
  background: #f0fff4;
  border: 1px solid #bbf7d0;
  padding: 3px 7px;
  display: inline-flex;
  align-items: center;
}

.vax-icon {
  height: 18px;   /* was 28px */
  width: auto;
  display: block;
}

.page-wrapper {
  max-width: 960px;
  margin: 0 auto;
  padding: 14px 24px 40px;  /* more bottom padding */
}

.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0;
  margin-top: 8px; margin-left: 7px;
}

.page-sub {
  font-size: 13px;
  color: #aaa;
  font-weight: 500;
  margin-top: 2px;
}

/* ── Profile row ── */
.profile-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  margin-bottom: 4px;
}

.pet-img {
  width: 160px;
  height: 200px;
  border-radius: 20px;
  object-fit: cover;
  border: 1px solid #f0e0ff;
  flex-shrink: 0;
  display: block;
}

.pet-info-col {
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}

.pet-name {
  font-size: 26px;
  font-weight: 800;
  color: #1a1a1a;
  letter-spacing: -0.4px;
  margin: 0 0 6px;
}

.pet-breed {
  font-size: 14px;
  color: #aaa;
  font-weight: 600;
  margin: 0 0 14px;
}

/* ── Badges ── */
.badges-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 11px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.badge-age        { background: #f3eeff; color: #6d28d9; }
.badge-male       { background: #eff6ff; color: #1d4ed8; }
.badge-female     { background: #fdf2f8; color: #be185d; }
.badge-vaccinated { background: #d4f5e2; color: #1a6b3c; }

/* ── Location ── */
.pet-location {
  font-size: 13px;
  color: #aaa;
  font-weight: 600;
  line-height: 1.5;
}

/* ── Divider ── */
.divider {
  height: 1px;
  background: #f0e0ff;
  margin: 28px 0;
}

/* ── Inner sections ── */
.inner-section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 12px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 10px;
}

.section-text {
  font-size: 15px;
  color: #555;
  line-height: 1.9;
  margin: 0;
}

/* ── Time slots ── */
.time-slots {
  display: flex;
  align-items: center;
  gap: 14px;
}

.time-slot {
  flex: 1;
  padding: 14px;
  background: #fffbe8;
  border: 1.5px solid #f0e0ff;
  border-radius: 14px;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #4a1070;
}

.time-sep {
  font-size: 18px;
  color: #ccc;
  font-weight: 700;
  flex-shrink: 0;
}

/* ── Loading ── */
.loading-text {
  text-align: center;
  color: #aaa;
  font-size: 14px;
  padding: 40px 0;
}

.days-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.day-badge {
  padding: 6px 13px;
  background: #f3eeff;
  border: 1px solid #dccffc;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: #6d28d9;
}

.calendar-button-wrapper {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
}

.calendar-button {
  width: 55px;
  height: 55px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  background: linear-gradient(135deg, #fffbe8, #fff8d3);
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s;
}

.calendar-button:hover {
  transform: scale(1.1);
  background: linear-gradient(135deg, #ffe876, #fffbe8);
  box-shadow: 0 6px 16px rgba(30,58,138,0.25);
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .page-wrapper { margin: 0 auto; }
}

@media (max-width: 640px) {
  .page-wrapper { padding: 18px 24px 60px; }
  .main-card    { width: 100%; padding: 24px 18px; }
  .profile-row  { flex-direction: column; align-items: center; text-align: center; }
  .pet-img      { width: 100%; height: 220px; }
  .badges-row   { justify-content: center; }
  .pet-name     { font-size: 22px; }
}

/* Replace your current mobile section for the pet details page */

@media (max-width: 480px) {

  .page-header { padding: 20px 16px 0 46px; }

  .page-wrapper {
    width: 100%;
    padding: 8px 6px 18px;
    height: 95vh;
  }

  .main-card {
    width: 100%;
    padding: 14px 12px;
    border-radius: 18px;
  }

  .profile-row {
    gap: 12px;
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
  }

  .pet-img {
    width: 100px;
    height: 110px;
    border-radius: 14px;
    flex-shrink: 0;
  }

  .badges-row {
    justify-content: flex-start;
    gap: 5px;
    margin-bottom: 8px;
  }

  .pet-name {
    font-size: 18px;
  }

  .pet-breed {
    font-size: 11px;
    margin-bottom: 8px;
  }

  .badge {
    font-size: 9px;
    padding: 3px 8px;
  }

  .pet-location {
    font-size: 11px;
  }

  .divider {
    margin: 16px 0;
  }

  .inner-section {
    margin-bottom: 16px;
  }

  .section-title {
    font-size: 10px;
    margin-bottom: 6px;
  }

  .section-text {
    font-size: 12px;
    line-height: 1.6;
  }

  .time-slots {
    gap: 8px;
  }

  .time-slot {
    padding: 10px 8px;
    font-size: 12px;
    border-radius: 10px;
  }

  .time-sep {
    font-size: 14px;
  }

  .book-btn {
    padding: 10px;
    font-size: 12px;
    border-radius: 10px;
    margin-top: auto;
  }
  .day-badge {
    font-size: 10px;
    padding: 4px 9px;
  }
}
</style>